export type ProjectViewMode = 'list' | 'board';
export type ProjectListSort = 'newest' | 'oldest' | 'priority';

export type ProjectViewPrefs = {
    /** Explicit list/board choice; null follows the project posture default. */
    view: ProjectViewMode | null;
    compact: boolean;
    sort: ProjectListSort;
    unassigned: boolean;
    assigneeId: string | null;
};

const STORAGE_PREFIX = 'yoda.projectViewPrefs';

export const DEFAULT_PROJECT_VIEW_PREFS: ProjectViewPrefs = {
    view: null,
    compact: false,
    sort: 'newest',
    unassigned: false,
    assigneeId: null,
};

const isViewMode = (value: unknown): value is ProjectViewMode =>
    value === 'list' || value === 'board';

const isSort = (value: unknown): value is ProjectListSort =>
    value === 'newest' || value === 'oldest' || value === 'priority';

export function projectViewPrefsStorageKey(projectId?: string | null): string | null {
    if (!projectId)
        return null;
    return `${STORAGE_PREFIX}.${projectId}`;
}

const normalizePrefs = (parsed: unknown): ProjectViewPrefs => {
    if (!parsed || typeof parsed !== 'object')
        return { ...DEFAULT_PROJECT_VIEW_PREFS };

    const raw = parsed as Record<string, unknown>;
    const assigneeId = typeof raw.assigneeId === 'string' && raw.assigneeId
        ? raw.assigneeId
        : null;

    return {
        view: isViewMode(raw.view) ? raw.view : null,
        compact: raw.compact === true,
        sort: isSort(raw.sort) ? raw.sort : DEFAULT_PROJECT_VIEW_PREFS.sort,
        unassigned: raw.unassigned === true && !assigneeId,
        assigneeId,
    };
};

export function loadProjectViewPrefs(projectId?: string | null): ProjectViewPrefs {
    try {
        const key = projectViewPrefsStorageKey(projectId);
        if (!key)
            return { ...DEFAULT_PROJECT_VIEW_PREFS };

        const raw = localStorage.getItem(key);
        if (!raw)
            return { ...DEFAULT_PROJECT_VIEW_PREFS };

        return normalizePrefs(JSON.parse(raw));
    } catch {
        return { ...DEFAULT_PROJECT_VIEW_PREFS };
    }
}

export function saveProjectViewPrefs(projectId: string | null | undefined, prefs: ProjectViewPrefs): void {
    try {
        const key = projectViewPrefsStorageKey(projectId);
        if (!key)
            return;

        localStorage.setItem(key, JSON.stringify(normalizePrefs(prefs)));
    } catch {
        // Ignore quota / private mode failures.
    }
}
